import { DateTime } from "luxon";

export const PERIOD_FILTER_KEY = "periodStart";
// Omnisearch alias for the same window; shared links may still carry it.
export const PERIOD_ALIAS_KEY = "period";
export const PERIOD_KEYS = [PERIOD_FILTER_KEY, PERIOD_ALIAS_KEY];

function parseMonth(value: string): DateTime | null {
  const month = DateTime.fromISO(value, { zone: "utc" });
  return month.isValid ? month.startOf("month") : null;
}

export function readPeriodFilter(
  value: string | undefined,
  defaultStart: DateTime
): [DateTime | null, DateTime | null] {
  if (!value) return [defaultStart, null];
  if (!value.includes("..")) {
    const month = parseMonth(value);
    return [month, month];
  }
  const [start = "", end = ""] = value.split("..", 2);
  return [start ? parseMonth(start) : null, end ? parseMonth(end) : null];
}

// A null bound is unbounded, so it never narrows the window.
function narrowerBound(
  a: DateTime | null,
  b: DateTime | null,
  pick: (a: DateTime, b: DateTime) => DateTime
): DateTime | null {
  return a && b ? pick(a, b) : (a ?? b);
}

// The backend ANDs every period term, so the effective window is their intersection.
export function readPeriodFilters(
  values: (string | undefined)[],
  defaultStart: DateTime
): [DateTime | null, DateTime | null] {
  const windows = values
    .filter((value) => value)
    .map((value) => readPeriodFilter(value, defaultStart));
  if (windows.length === 0) return [defaultStart, null];
  return windows.reduce(([start, end], [nextStart, nextEnd]) => [
    narrowerBound(start, nextStart, (a, b) => (a.toMillis() >= b.toMillis() ? a : b)),
    narrowerBound(end, nextEnd, (a, b) => (a.toMillis() <= b.toMillis() ? a : b)),
  ]);
}

function formatMonth(value: DateTime | null): string {
  return value?.isValid ? value.startOf("month").toFormat("yyyy-MM-dd") : "";
}

export function writePeriodFilter(start: DateTime | null, end: DateTime | null): string {
  const startValue = formatMonth(start);
  const endValue = formatMonth(end);
  return `${startValue}..${endValue}`;
}
